/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import {
  fetchSurveys,
  updateSurvey,
  deleteSurvey as _deleteSurvey,
} from "../../../api";
import { RootState } from "../../store";
import { Edits, Survey, LoadStatus } from "../../../types";

export interface SurveysState {
  surveys: Record<string, Survey>;
  surveyEdits: Record<string, Edits<Survey>>;
  surveyLoadStatus: LoadStatus;
}

const initialState: SurveysState = {
  surveys: {},
  surveyEdits: {},
  surveyLoadStatus: LoadStatus.NONE,
};

/** Actions */

export const loadSurveys = createAsyncThunk(
  "surveys/loadSurveys",
  async (_, thunkAPI): Promise<Survey[]> => {
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    return await fetchSurveys();
  }
);

export const addOrUpdateSurvey = createAsyncThunk(
  "surveys/addOrUpdateSurvey",
  async (data: Partial<Survey>, thunkAPI): Promise<Survey> => {
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    return await updateSurvey(state.login.accessToken, data);
  }
);

export const deleteSurvey = createAsyncThunk(
  "surveys/deleteSurvey",
  async (id: string, thunkAPI): Promise<void> => {
    if (!id) throw new Error("id required");
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    await _deleteSurvey(state.login.accessToken, id);
  }
);

export const revertSurvey = createAsyncThunk(
  "surveys/revertSurvey",
  async (id: string, thunkAPI): Promise<Survey> => {
    if (!id) throw new Error("id required");
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken)
      throw new Error("login required");
    const edits = state.surveys.surveyEdits[id];
    if (
      !edits ||
      !edits.oldData ||
      `${JSON.stringify(edits.oldData)}` === `${JSON.stringify(edits.newData)}`
    ) {
      throw new Error("no changes to revert");
    }
    return await updateSurvey(state.login.accessToken, edits.oldData);
  }
);

export const surveysSlice = createSlice({
  name: "surveys",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // load surveys
      .addCase(loadSurveys.pending, (state) => {
        state.surveyLoadStatus = LoadStatus.IN_PROGRESS;
      })
      .addCase(loadSurveys.fulfilled, (state, action) => {
        for (const survey of action.payload) {
          state.surveys[survey.id] = survey;
          state.surveyEdits[survey.id] = {
            oldData: survey,
          };
        }
        state.surveyLoadStatus = LoadStatus.DONE;
      })
      .addCase(loadSurveys.rejected, (state) => {
        state.surveyLoadStatus = LoadStatus.FAILED;
      })
      // add or update survey
      .addCase(addOrUpdateSurvey.fulfilled, (state, action) => {
        const survey = action.payload;
        if (state.surveys[survey.id]) {
          state.surveyEdits[survey.id].newData = survey;
        } else {
          state.surveyEdits[survey.id] = {
            oldData: survey,
            created: true,
          };
        }
        state.surveys[survey.id] = survey;
      })
      // delete survey
      .addCase(deleteSurvey.fulfilled, (state, action) => {
        const id = action.meta.arg;
        delete state.surveys[id];
      })
      // revert survey
      .addCase(revertSurvey.fulfilled, (state, action) => {
        const id = action.meta.arg;
        state.surveys[id] = action.payload;
        state.surveyEdits[id].oldData = action.payload;
        state.surveyEdits[id].newData = undefined;
      });
  },
});

export default surveysSlice.reducer;
